import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { HeaderBreadcrumbs, Iconify, Page } from '@/components';
import { FormProvider, RHFSwitch, RHFTextField } from '@/components/hook-form';
import { SkeletonLoadingTableData } from '@/components/skeleton';
import useResponsive from '@/hooks/useResponsive';
import useSettings from '@/hooks/useSettings';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { PATH_USER } from '@/features/app';
import { createInAppPopup, getInAppPopup, updateInAppPopup } from '../api';
import { inAppPopupsPath, ROOTS_IN_APP_POPUPS } from '../routes/paths';
import { InAppPopupFormValues } from '../types';
import {
  createDefaultPopupButton,
  inAppPopupDefaultValues,
  inAppPopupFormToPayload,
  inAppPopupPayloadForUpdate,
  inAppPopupToDuplicateFormValues,
  inAppPopupToFormValues,
  InAppPopupSchema,
  IN_APP_POPUP_ACTION_TYPE_OPTIONS,
  IN_APP_POPUP_BUTTON_STYLE_OPTIONS,
  IN_APP_POPUP_PLATFORM_OPTIONS,
  IN_APP_POPUP_ROUTE_OPTIONS,
} from '../utils';

export default function InAppPopupCreateEdit() {
  const navigate = useNavigate();
  const { popupId } = useParams();
  const [searchParams] = useSearchParams();
  const { themeStretch } = useSettings();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();
  const mdDown = useResponsive('down', 'md');

  const methods = useForm<InAppPopupFormValues>({
    resolver: yupResolver(InAppPopupSchema),
    defaultValues: inAppPopupDefaultValues,
  });

  const {
    control,
    getValues,
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const {
    fields: buttonFields,
    append: appendButton,
    remove: removeButton,
    move: moveButton,
  } = useFieldArray({
    control,
    name: 'buttons',
  });

  const buttons = useWatch({ control, name: 'buttons' }) ?? [];
  const duplicateFromId = popupId
    ? ''
    : searchParams.get('duplicateFrom') ?? '';
  const popupToLoadId = popupId ?? duplicateFromId;
  const isDuplicating = !!duplicateFromId;

  const { data: currentPopup, isLoading: isCurrentPopupLoading } = useQuery({
    queryKey: [
      'in-app-popups',
      popupToLoadId,
      isDuplicating ? 'duplicate' : 'edit',
    ],
    queryFn: () => getInAppPopup(String(popupToLoadId)),
    onSuccess: (popup) => {
      reset(
        isDuplicating
          ? inAppPopupToDuplicateFormValues(popup)
          : inAppPopupToFormValues(popup)
      );
    },
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching popup data');
    },
    enabled: !!popupToLoadId,
  });

  const stackDirection = mdDown ? 'column' : 'row';

  const submitPopup = async (values: InAppPopupFormValues) => {
    try {
      if (popupId) {
        await updateInAppPopup(popupId, inAppPopupPayloadForUpdate(values));
        successMsg(`Popup ${popupId} updated successfully`);
      } else {
        await createInAppPopup(inAppPopupFormToPayload(values));
        successMsg('Popup created successfully');
      }

      queryClient.invalidateQueries(['in-app-popups']);
      navigate(inAppPopupsPath('list'));
    } catch (error) {
      errorMsg(
        error,
        `Something went wrong while ${popupId ? 'updating' : 'creating'} popup`
      );
    }
  };

  const handleActionTypeChange = (
    index: number,
    value: InAppPopupFormValues['buttons'][number]['action_type'],
    onChange: (value: string) => void
  ) => {
    onChange(value);

    if (value === 'route' && !getValues(`buttons.${index}.route`)) {
      setValue(`buttons.${index}.route`, IN_APP_POPUP_ROUTE_OPTIONS[0].value);
    }
  };

  const showSkeleton =
    !!popupToLoadId && (isCurrentPopupLoading || !currentPopup);
  const pageModeLabel = isDuplicating
    ? 'Duplicate popup'
    : !popupId
    ? 'Create a new popup'
    : 'Edit popup';

  return (
    <Page title="In-app Popups: Create/edit">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={pageModeLabel}
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'In-app Popups', href: ROOTS_IN_APP_POPUPS },
            { name: pageModeLabel },
          ]}
        />

        {showSkeleton ? (
          <SkeletonLoadingTableData />
        ) : (
          <Card sx={{ py: 2, px: 4 }}>
            <FormProvider
              methods={methods}
              onSubmit={handleSubmit(submitPopup)}
            >
              <Stack spacing={3}>
                <Stack spacing={2}>
                  <Typography fontSize={20} fontWeight={600}>
                    Popup details
                  </Typography>

                  <Stack direction={stackDirection} spacing={2}>
                    <RHFTextField
                      name="id"
                      label="ID"
                      disabled={!!popupId}
                      placeholder="summer-offer-2026"
                    />
                    <RHFTextField name="title" label="Title" />
                    <RHFTextField
                      name="priority"
                      label="Priority"
                      type="number"
                    />
                  </Stack>

                  <RHFTextField
                    name="body"
                    label="Body"
                    multiline
                    minRows={3}
                  />

                  <Stack direction={stackDirection} spacing={2}>
                    <RHFTextField
                      name="banner_image_url"
                      label="Banner image URL"
                    />
                    <RHFTextField name="image_alt" label="Image alt text" />
                  </Stack>

                  <Stack direction={stackDirection} spacing={2}>
                    <Controller
                      name="starts_at"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          type="datetime-local"
                          label="Starts at"
                          InputLabelProps={{ shrink: true }}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                    <Controller
                      name="expires_at"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          type="datetime-local"
                          label="Expires at"
                          InputLabelProps={{ shrink: true }}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Stack>

                  <Stack
                    direction={stackDirection}
                    spacing={2}
                    alignItems={mdDown ? 'flex-start' : 'center'}
                  >
                    <RHFSwitch name="active" label="Active" />
                    <RHFSwitch name="guest_eligible" label="Guest eligible" />
                    <Controller
                      name="platforms"
                      control={control}
                      render={({ field, fieldState: { error } }) => {
                        const selectedPlatforms = field.value ?? [];

                        return (
                          <FormControl error={!!error}>
                            <FormGroup row>
                              {IN_APP_POPUP_PLATFORM_OPTIONS.map((platform) => (
                                <FormControlLabel
                                  key={platform.value}
                                  control={
                                    <Checkbox
                                      checked={selectedPlatforms.includes(
                                        platform.value
                                      )}
                                      onChange={(event) => {
                                        const nextPlatforms = event.target
                                          .checked
                                          ? [
                                              ...selectedPlatforms,
                                              platform.value,
                                            ]
                                          : selectedPlatforms.filter(
                                              (value) =>
                                                value !== platform.value
                                            );
                                        field.onChange(nextPlatforms);
                                      }}
                                    />
                                  }
                                  label={platform.label}
                                />
                              ))}
                            </FormGroup>
                            <FormHelperText>
                              Leave unchecked for all platforms
                            </FormHelperText>
                          </FormControl>
                        );
                      }}
                    />
                  </Stack>
                </Stack>

                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography fontSize={20} fontWeight={600}>
                      Buttons
                    </Typography>
                    <Tooltip title="Add button">
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Iconify icon="eva:plus-fill" />}
                          disabled={buttonFields.length >= 2}
                          onClick={() =>
                            appendButton(createDefaultPopupButton())
                          }
                        >
                          Add Button
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>

                  {buttonFields.map((field, index) => {
                    const actionType = buttons[index]?.action_type ?? 'dismiss';

                    return (
                      <Box
                        key={field.id}
                        sx={{
                          border: (theme) =>
                            `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          p: 2,
                        }}
                      >
                        <Stack spacing={2}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="subtitle2">
                              Button {index + 1}
                            </Typography>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Move button up">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={index === 0}
                                    onClick={() => moveButton(index, index - 1)}
                                  >
                                    <Iconify icon="eva:arrow-upward-fill" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Move button down">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={index === buttonFields.length - 1}
                                    onClick={() => moveButton(index, index + 1)}
                                  >
                                    <Iconify icon="eva:arrow-downward-fill" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Remove button">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => removeButton(index)}
                                  >
                                    <Iconify icon="eva:trash-2-outline" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </Stack>

                          <Stack direction={stackDirection} spacing={2}>
                            <RHFTextField
                              name={`buttons.${index}.label`}
                              label="Label"
                            />
                            <RHFTextField
                              name={`buttons.${index}.style`}
                              label="Style"
                              select
                            >
                              {IN_APP_POPUP_BUTTON_STYLE_OPTIONS.map(
                                (option) => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </MenuItem>
                                )
                              )}
                            </RHFTextField>
                            <Controller
                              name={`buttons.${index}.action_type`}
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <FormControl
                                  fullWidth
                                  size="small"
                                  error={!!error}
                                >
                                  <InputLabel id={`${field.name}-label`}>
                                    Action
                                  </InputLabel>
                                  <Select
                                    {...field}
                                    labelId={`${field.name}-label`}
                                    label="Action"
                                    onChange={(event) =>
                                      handleActionTypeChange(
                                        index,
                                        event.target
                                          .value as InAppPopupFormValues['buttons'][number]['action_type'],
                                        field.onChange
                                      )
                                    }
                                  >
                                    {IN_APP_POPUP_ACTION_TYPE_OPTIONS.map(
                                      (option) => (
                                        <MenuItem
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </MenuItem>
                                      )
                                    )}
                                  </Select>
                                  <FormHelperText>
                                    {error?.message}
                                  </FormHelperText>
                                </FormControl>
                              )}
                            />
                          </Stack>

                          {actionType === 'route' && (
                            <RHFTextField
                              name={`buttons.${index}.route`}
                              label="Route"
                              select
                            >
                              {IN_APP_POPUP_ROUTE_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </RHFTextField>
                          )}

                          {actionType === 'external_url' && (
                            <RHFTextField
                              name={`buttons.${index}.external_url`}
                              label="External URL"
                              placeholder="https://example.com"
                            />
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>

                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                  <Button
                    color="inherit"
                    onClick={() => navigate(inAppPopupsPath('list'))}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                  >
                    {popupId ? 'Update' : 'Create'} Popup
                  </LoadingButton>
                </Stack>
              </Stack>
            </FormProvider>
          </Card>
        )}
      </Container>
    </Page>
  );
}
